import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useIsMobile from '../../../utils/useIsMobile'
import { FILTER_SCREEN_MOBILE, MOBILE_DIVIDER_OFFSET } from '../../../utils/variables'
import { createPortal } from 'react-dom'
import { Button, Drawer, IconButton, Spinner, Typography } from '@material-tailwind/react'
import CloseDrawerIcon from '../../../images/icons/CloseDrawerIcon'
import FilterItem from '../../tableHelpers/filters/FilterItem'
import BtnFilter from '../../tableHelpers/filters/BtnFilter'
import SelectIcon from '../../../images/icons/SelectIcon'
import { isIterableArray, toFormatPrice } from '../../../utils/utils'
import { useForm, useWatch } from 'react-hook-form'
import FilterDateItem from '../../tableHelpers/filters/FilterDateItem'
import moment from 'moment'
import { useGetCompaniesPeriodQuery, useViewBalancesQuery } from '../../../store/auth/authService'
import CalendarDaySelect from './CalendarDaySelect'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import CalendarIcon from '../../../images/icons/CalendarIcon'
import OverlayUnTouchable from '../../../layouts/pagesLayouts/OverlayUnTouchable'

type FormInput = {
    day: string,
    period: string,
    ids: [],
    service_id: number
}

const DrawerViewBalances = (props: {
    open: boolean, handleClose: any, legalItems?: any, contracts: any, contractItems?: any
}): any => {

    const { open, handleClose, legalItems, contracts, contractItems } = props

    const { t } = useTranslation()

    const { token } = useSelector((state: RootState) => state.auth)
    const { service_id } = useSelector((state: RootState) => state.contract)

    const isMobile = useIsMobile(FILTER_SCREEN_MOBILE)
    const placement = isMobile ? 'bottom' : 'right'
    const size = isMobile ? window.innerHeight - MOBILE_DIVIDER_OFFSET : 700


    const defaultState: FormInput = {
        day: moment().format('DD'),
        period: moment().format('MM-YYYY'),
        ids: [],
        service_id: service_id
    }
    const {
        control,
        reset,
        setValue,
        getValues,
    } = useForm<FormInput>({
        defaultValues: defaultState,
    })

    const [sendForm, setSendForm] = useState<FormInput>(defaultState);

    const periodWatch = useWatch({ control, name: 'period' });
    const dayWatch = useWatch({ control, name: 'day' });

    const [filtersState, setFiltersState] = useState<FormInput>(defaultState);

    const { currentData, isLoading, isFetching, isError, refetch } = useViewBalancesQuery(sendForm, {
        skip: !isIterableArray(legalItems)
    })

    const datesData = useGetCompaniesPeriodQuery({
        ids: contracts?.reduce((acc: any[], contract: any) => {
            if (isIterableArray(contract.items)) {
                contract.items.forEach((item: any) => {
                    acc.push(item.id)
                });
            }
            return acc
        }, [])
    }, {
        skip: !isIterableArray(legalItems) || !isIterableArray(contracts)
    })

    useEffect(() => {
        if (isIterableArray(legalItems) && open) fetchPage(filtersState)
    }, [legalItems, open]);

    useEffect(() => {
        if (!open) setValue('ids', [])
    }, [open]);


    const fetchPage = (values: any) => {

        const ids = isIterableArray(values.ids) ? values.ids : legalItems?.map((i: any) => i.value)

        const contract_ids = contractItems ? contractItems.map((i: any) => i.value) : ids.reduce((acc: any[], id: number) => {
            const conItems = isIterableArray(contracts) ? contracts.find((contractItem: any) => contractItem.id === id)?.items : []
            conItems.forEach((element: any) => {
                acc.push(element.id)
            });
            return acc
        }, [])


        const newForm = {
            day: values.day,
            period: values.period,
            service_id: service_id,
            ids: contract_ids
        }
        setSendForm(newForm)

    }


    const handleCloseFilter = () => {
        const values = getValues()

        setFiltersState(values)

        if (values.day)
            if (JSON.stringify(values) !== JSON.stringify(filtersState)) {
                // fetch page with new filters 
                fetchPage(values)
            }
    }

    const setDay = (day: any) => {
        setValue('day', day)
        handleCloseFilter()
    }


    return (
        createPortal(
            <Drawer
                placement={placement}
                size={size}
                open={open}
                onClose={handleClose}
                className={`asdasd p-4 ${open ? 'tab:!right-[12px] tab:!top-[12px] tab:!bottom-[-12px] opacity-1' : 'tab:!right-0 opacity-0 duration-[250ms]'} left-0 right-0  tab:!left-auto !bottom-0  tab:!max-w-[700px]  flex flex-col overflow-auto`}
            >
                <div className='flex flex-col h-[100%]'>
                    <div className="mb-6 flex items-center justify-between">
                        <Typography variant="lead" className='text-[18px] tab:text-[24px] font-medium' >
                            {service_id === 1 ? t('view_balances_drawer_title_ee') : service_id === 2 ? t('view_balances_drawer_title_gas') : ' '}
                        </Typography>
                        <IconButton variant="text" color="blue-gray" className='hover:bg-gray-100 active:bg-gray-100 rounded-full' onClick={handleClose}>
                            <CloseDrawerIcon />
                        </IconButton>
                    </div>
                    <div className='flex flex-col justify-between grow'>
                        {
                            isIterableArray(contractItems) ?
                                <FilterItem
                                    isLoadingFetch={isFetching}
                                    control={control}
                                    label={t('filters.companies_ids')}
                                    fieldName={'ids'}
                                    defaultValue={defaultState.ids}
                                    filtersData={contractItems}
                                    handleCloseFilter={handleCloseFilter}
                                    contClassName='!w-full'
                                    listClassName={'!w-[630px] z-[10000]'}
                                />
                                :
                                isIterableArray(legalItems) ?
                                    <FilterItem
                                        isLoadingFetch={isFetching}
                                        control={control}
                                        label={t('filters.companies_ids')}
                                        fieldName={'ids'}
                                        defaultValue={defaultState.ids}
                                        filtersData={legalItems}
                                        handleCloseFilter={handleCloseFilter}
                                        contClassName='!w-full'
                                        listClassName={'!w-[630px] z-[10000]'}
                                    />
                                    :
                                    <BtnFilter
                                        icon={<SelectIcon />}
                                        isLoading={true}
                                    >
                                        {t('filters.companies_ids')}
                                    </BtnFilter>
                        }
                        {
                            isFetching ?
                                <Spinner className='mx-auto' />
                                :
                                currentData ?
                                    <div className='grid grid-cols-2 justify-center items-center gap-[64px]'>
                                        {
                                            Object.keys(currentData).map(key => (
                                                <div key={key} className='flex flex-col items-center gap-[12px]'>
                                                    <Typography variant='lead'>
                                                        {t(key)}
                                                    </Typography>
                                                    <Typography variant='h3'>
                                                        {toFormatPrice(currentData[key])}
                                                    </Typography>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    :
                                    isError ?
                                        <div className='mx-auto'>
                                            <div className="flex flex-col items-center gap-[40px] my-[40px]">
                                                <Typography variant='lead' className='des:text-[20px] font-medium text-center '>
                                                    {t('tables.error_on_loading')}
                                                </Typography>
                                                <Button color='black' onClick={refetch}>
                                                    {t('reload')}
                                                </Button>
                                            </div>
                                        </div>
                                        :
                                        <>
                                        </>
                        }
                        <div className='flex flex-col gap-[12px]'>
                            {
                                datesData.isFetching ?
                                    <div className={` tab:w-[200px] des:w-[224px] `}>
                                        <BtnFilter
                                            icon={<CalendarIcon />}
                                            isLoading={true}
                                        >
                                            {t("filters.period_label")}
                                        </BtnFilter>
                                    </div>
                                    :
                                    <FilterDateItem
                                        isLoadingFetch={false}
                                        placement={'bottom-start'}
                                        control={control}
                                        fieldName={'period'}
                                        label={t("filters.period_label")}
                                        datesData={datesData.currentData}
                                        handleCloseFilter={() => {
                                            setValue('day', '')
                                            handleCloseFilter()
                                        }
                                        }
                                        isDisabled={(date: string) => {
                                            return false
                                        }}
                                        valueLabelFormatter={(label: string) => {
                                            const lab = moment(label, 'MM-YYYY').format("MMMM YYYY")
                                            return <span>{String(lab).charAt(0).toUpperCase() + String(lab).slice(1)}</span>
                                        }}
                                    />

                            }
                            <OverlayUnTouchable isOverlay={!isIterableArray(legalItems)}>
                                <CalendarDaySelect
                                    period={periodWatch}
                                    day={dayWatch}
                                    setDay={setDay}
                                />
                            </OverlayUnTouchable>
                        </div>
                        <Button color='white' className='mb-[40px] tab:hidden'>
                            {t('close')}
                        </Button>
                    </div>
                </div>

            </Drawer>,
            document.getElementById('root')!
        )
    )
}

export default DrawerViewBalances